import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SearchableOption<T = unknown> {
  value: T;
  label: string;
  /** Optional extra info shown under the label. */
  sub?: string;
}

/**
 * A tiny dropdown control that looks like our `.form-select` but filters
 * options as you type. Implements ControlValueAccessor so it drops into
 * reactive forms with `formControlName`.
 */
@Component({
  selector: 'bc-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableSelectComponent<T = unknown> implements ControlValueAccessor {
  @Input() options: SearchableOption<T>[] = [];
  @Input() placeholder = 'Select…';
  /** Label for an explicit "none/clear" row at the top of the panel. */
  @Input() emptyLabel: string | null = null;
  /** When set, an "Add new" affordance is rendered at the bottom of the panel. */
  @Input() addLabel: string | null = null;
  /** When true, an extra "Loading…" line is shown above the list. */
  @Input() loading = false;
  /**
   * When true, the user can commit whatever they typed as a free-text value
   * (used by Industry: pick a known one, or type your own if not listed).
   */
  @Input() allowCustom = false;

  @Output() addClicked = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  open = false;
  query = '';
  selectedValue: T | null = null;
  disabled = false;

  private onChangeFn: (v: T | null) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor(private host: ElementRef<HTMLElement>) {}

  // ─── Computed ───
  get selectedLabel(): string {
    if (this.selectedValue === null || this.selectedValue === undefined || this.selectedValue === ('' as unknown as T)) return '';
    const match = this.options.find((o) => o.value === this.selectedValue);
    if (match) return match.label;
    // Free-text / custom value not present in the option list.
    return typeof this.selectedValue === 'string' ? this.selectedValue : '';
  }

  get filtered(): SearchableOption<T>[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.options;
    return this.options.filter(
      (o) =>
        (o.label || '').toLowerCase().includes(q) ||
        (o.sub || '').toLowerCase().includes(q),
    );
  }

  /** Show the "Use '<query>'" row when free-text is allowed and the typed
   *  text doesn't exactly match an existing option label. */
  get showCustomRow(): boolean {
    const q = this.query.trim();
    if (!this.allowCustom || !q) return false;
    return !this.options.some((o) => (o.label || '').toLowerCase() === q.toLowerCase());
  }

  trackByValue = (_: number, o: SearchableOption<T>) => o.value as unknown as string;

  selectCustom(): void {
    const q = this.query.trim();
    if (q) this.select(q as unknown as T);
  }

  // ─── ControlValueAccessor ───
  writeValue(value: T | null): void {
    this.selectedValue = value ?? null;
  }
  registerOnChange(fn: (v: T | null) => void): void { this.onChangeFn = fn; }
  registerOnTouched(fn: () => void): void { this.onTouchedFn = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  // ─── Interaction ───
  toggle(): void {
    if (this.disabled) return;
    this.open = !this.open;
    if (this.open) {
      this.query = '';
      // Focus the search input after the panel renders.
      setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
    }
    this.onTouchedFn();
  }

  close(): void {
    if (this.open) this.open = false;
  }

  select(value: T | null): void {
    this.selectedValue = value;
    this.onChangeFn(value);
    this.close();
  }

  onAddClick(event: MouseEvent): void {
    event.stopPropagation();
    this.close();
    this.addClicked.emit();
  }

  // ─── Outside-click + Escape close ───
  @HostListener('document:mousedown', ['$event'])
  onDocMouseDown(ev: MouseEvent): void {
    if (!this.open) return;
    if (!this.host.nativeElement.contains(ev.target as Node)) this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.close(); }
}
